import React, { useState } from 'react';
import { Table, Button, Space, Input, Modal, Popconfirm, Tag, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useFurnizori } from '../hooks/useFurnizori';
import FurnizorForm from './FurnizorForm';
import FurnizorDetails from './FurnizorDetails';

const { Search } = Input;

function FurnizoriList() {
  const { furnizori, loading, pagination, fetchFurnizori, createFurnizor, updateFurnizor, deleteFurnizor } = useFurnizori();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailsVisible, setIsDetailsVisible] = useState(false);
  const [editingFurnizor, setEditingFurnizor] = useState(null);
  const [selectedFurnizor, setSelectedFurnizor] = useState(null);
  const [searchText, setSearchText] = useState('');
  const navigate = useNavigate();
  const columns = [
    {
      title: 'Denumire',
      dataIndex: 'denumire',
      key: 'denumire',
      sorter: true,
      render: (text, record) => (
        <div className='flex items-center gap-2'>
          <div>
            <div className="font-medium text-gray-900">{text}</div>
            <div className="text-sm text-gray-500">{record._id}</div>
            {record.id_no && (
              <div className="text-sm text-gray-500">CF: {record.id_no}</div>
            )}
          </div>
          <div>
            <Button type='link' size='small' onClick={() => navigate(`furnizor/${record._id}`)}>
              <EyeOutlined />
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      dataIndex: 'contact',
      key: 'contact',
      render: (contact) => {
        const principalContact = contact?.find(c => c.principal_dahnu) || contact?.[0];
        return principalContact ? (
          <div>
            <div className="text-sm font-medium">{principalContact.nume_prenume}</div>
            <div className="text-sm text-gray-500">{principalContact.telefon}</div>
            <div className="text-sm text-gray-500">{principalContact.email}</div>
          </div>
        ) : '-';
      },
    },
    {
      title: 'Adresă',
      dataIndex: 'adresa_juridica',
      key: 'adresa_juridica',
      render: (adresa) => (
        <Tooltip title={adresa}>
          <div className="truncate max-w-xs">{adresa || '-'}</div>
        </Tooltip>
      ),
    },
    {
      title: 'Termeni',
      key: 'termeni',
      render: (_, record) => (
        <div className="text-sm">
          {record.termen_de_plata && (
            <div>Plată: {record.termen_de_plata}</div>
          )}
          {record.termen_de_livrare && (
            <div>Livrare: {record.termen_de_livrare}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Website',
      dataIndex: 'website',
      key: 'website',
      render: (website) => website ? (
        <a href={website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
          {website}
        </a>
      ) : '-',
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => {
              setSelectedFurnizor(record);
              setIsDetailsVisible(true);
            }}
          />
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => {
              setEditingFurnizor(record);
              setIsModalVisible(true);
            }}
          />
          <Popconfirm
            title="Ești sigur că vrei să ștergi acest furnizor?"
            onConfirm={() => deleteFurnizor(record._id)}
            okText="Da"
            cancelText="Nu"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleTableChange = (paginationInfo, filters, sorter) => {
    fetchFurnizori(paginationInfo.current, paginationInfo.pageSize, {
      search: searchText,
      ...filters
    });
  };

  const handleSearch = (value) => {
    setSearchText(value);
    fetchFurnizori(1, pagination.pageSize, { search: value });
  };

  const handleModalSubmit = async (values) => {
    let success;
    if (editingFurnizor) {
      success = await updateFurnizor(editingFurnizor._id, values);
    } else {
      success = await createFurnizor(values);
    }
    
    if (success) {
      setIsModalVisible(false);
      setEditingFurnizor(null);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Furnizori</h2>
          <p className="text-gray-600">Gestionează furnizorii companiei</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingFurnizor(null);
            setIsModalVisible(true);
          }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          Adaugă Furnizor
        </Button>
      </div>

      <div className="mb-4">
        <Search
          placeholder="Caută furnizori..."
          allowClear
          enterButton={<SearchOutlined />}
          size="large"
          onSearch={handleSearch}
          className="max-w-md"
        />
      </div>

      <Table
        columns={columns}
        dataSource={furnizori}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} din ${total} furnizori`,
        }}
        onChange={handleTableChange}
        className="bg-white rounded-lg shadow-sm"
        scroll={{ x: 1200 }}
      />

      <Modal
        title={editingFurnizor ? 'Editează Furnizor' : 'Adaugă Furnizor Nou'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingFurnizor(null);
        }}
        footer={null}
        width={1000}
        destroyOnClose
      >
        <FurnizorForm
          initialValues={editingFurnizor}
          onSubmit={handleModalSubmit}
          onCancel={() => {
            setIsModalVisible(false);
            setEditingFurnizor(null);
          }}
        />
      </Modal>

      <FurnizorDetails
        visible={isDetailsVisible}
        furnizor={selectedFurnizor}
        onClose={() => {
          setIsDetailsVisible(false);
          setSelectedFurnizor(null);
        }}
      />
    </div>
  );
}

export default FurnizoriList; 